// 多多拼 · 朱印运营台 基础组件：印章状态、筛选胶囊、指标条、工作面、人员单元格、确认对话框。
import React from "react";
import dayjs from "dayjs";
import { Button, Modal, Pagination } from "antd";
import Icon, { IconName } from "./Icon";
import "./ui.scss";

export { default as Icon } from "./Icon";
export type { IconName } from "./Icon";

/* ---------------- 状态「印」 ---------------- */
export type Tone = "ink" | "indigo" | "sage" | "ochre" | "ver";

export const StatusBadge: React.FC<{
  tone?: Tone;
  dot?: boolean;
  size?: "sm" | "md";
  children: React.ReactNode;
  title?: string;
}> = ({ tone = "ink", dot = true, size = "md", children, title }) => (
  <span className={`ddp-badge ddp-badge--${tone}${size === "sm" ? " ddp-badge--sm" : ""}`} title={title}>
    {dot && <i aria-hidden="true" />}
    {children}
  </span>
);

type Vocab = Record<string, [string, Tone]>;

// 统一状态词（三端一致）
export const TASK_STATUS: Vocab = {
  pending: ["等待中", "ink"],
  waiting: ["等待中", "ink"],
  running: ["执行中", "indigo"],
  completed: ["已完成", "sage"],
  partial: ["部分完成", "ochre"],
  failed: ["失败", "ver"],
  cancelled: ["已取消", "ink"],
  expired: ["已过期", "ink"],
  paused: ["已暂停", "ochre"],
};

export const TASK_TYPE_LABEL: Record<string, string> = {
  discount_70: "防比价报名",
  original_price_promotion: "原价卡大促",
  fission_normal: "店铺商品裂变",
  fission_competitor: "同行截流裂变",
  fission_opportunity: "机会商品裂变",
  sku_words: "SKU 混淆",
  price_adjust: "价格调整",
};

export const ROLE: Vocab = {
  admin: ["管理员", "ver"],
  org_manager: ["组织管理员", "ochre"],
  staff: ["员工", "ink"],
  customer: ["客户", "ink"],
};

export const ORG_STATUS: Vocab = {
  active: ["活跃", "sage"],
  inactive: ["非活跃", "ink"],
  suspended: ["已暂停", "ver"],
};

export const CREDIT_OP: Vocab = {
  consume: ["消耗", "ink"],
  recharge: ["充值", "sage"],
  activity_gift: ["活动赠送", "ochre"],
  register_gift: ["注册赠送", "indigo"],
};

export const VocabBadge: React.FC<{ vocab: Vocab; value?: string | null; fallback?: string }> = ({ vocab, value, fallback = "—" }) => {
  if (!value) return <span className="text-faint">{fallback}</span>;
  const hit = vocab[value];
  return <StatusBadge tone={hit ? hit[1] : "ink"}>{hit ? hit[0] : value}</StatusBadge>;
};

/* ---------------- 筛选胶囊 ---------------- */
export interface ChipItem<K extends string = string> {
  key: K;
  label: React.ReactNode;
  count?: number | string;
}

export function FilterChips<K extends string>({
  items,
  value,
  onChange,
  ariaLabel = "筛选",
}: {
  items: ChipItem<K>[];
  value: K;
  onChange: (key: K) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="ddp-chips" role="tablist" aria-label={ariaLabel}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={item.key === value}
          className={`ddp-chip${item.key === value ? " is-active" : ""}`}
          onClick={() => onChange(item.key)}
        >
          {item.label}
          {item.count !== undefined && <span className="ddp-chip__count">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------- 指标条 ---------------- */
export interface MetricCell<K extends string = string> {
  key: K;
  label: string;
  value: React.ReactNode;
  note?: string;
  dot?: string;
}

export function MetricStrip<K extends string>({
  cells,
  active,
  onSelect,
  ariaLabel = "按状态筛选",
}: {
  cells: MetricCell<K>[];
  active?: K;
  onSelect?: (key: K) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="ddp-metrics" role="tablist" aria-label={ariaLabel} style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
      {cells.map((cell) => (
        <button
          key={cell.key}
          type="button"
          role="tab"
          aria-selected={cell.key === active}
          className={`ddp-metric${cell.key === active ? " is-active" : ""}`}
          onClick={() => onSelect?.(cell.key)}
        >
          <span className="ddp-metric__label">
            {cell.dot && <i style={{ background: cell.dot }} aria-hidden="true" />}
            {cell.label}
          </span>
          <span className="ddp-metric__value">
            <strong>{cell.value}</strong>
            {cell.note && <small>{cell.note}</small>}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ---------------- 工作面：工具条 + 表格 + 分页 ---------------- */
export const Surface: React.FC<{
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ toolbar, footer, className, children }) => (
  <section className={`ddp-surface${className ? ` ${className}` : ""}`}>
    {toolbar && <div className="ddp-surface__toolbar">{toolbar}</div>}
    <div className="ddp-surface__body">{children}</div>
    {footer && <div className="ddp-surface__footer">{footer}</div>}
  </section>
);

export const ToolbarGroup: React.FC<{ children: React.ReactNode; grow?: boolean }> = ({ children, grow }) => (
  <div className={`ddp-toolbar-group${grow ? " is-grow" : ""}`}>{children}</div>
);

export const Pager: React.FC<{
  total: number;
  current: number;
  pageSize: number;
  onChange: (page: number, pageSize: number) => void;
  pageSizeOptions?: number[];
}> = ({ total, current, pageSize, onChange, pageSizeOptions = [10, 20, 50] }) => (
  <div className="ddp-pager">
    <span className="ddp-pager__total">
      共 <b className="mono">{total.toLocaleString("zh-CN")}</b> 条
    </span>
    <Pagination
      current={current}
      pageSize={pageSize}
      total={total}
      showSizeChanger
      pageSizeOptions={pageSizeOptions.map(String)}
      onChange={onChange}
      hideOnSinglePage={false}
    />
  </div>
);

/* ---------------- 单元格 ---------------- */
export const Avatar: React.FC<{ text?: string; size?: number; tone?: "paper" | "ink" | "ver" }> = ({ text, size = 32, tone = "paper" }) => (
  <span className={`ddp-avatar ddp-avatar--${tone}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }} aria-hidden="true">
    {(text || "多").slice(0, 1)}
  </span>
);

export const PersonCell: React.FC<{ name?: string; sub?: string; tone?: "paper" | "ink" | "ver"; onClick?: () => void }> = ({ name, sub, tone, onClick }) => {
  const inner = (
    <>
      <Avatar text={name || sub} tone={tone} />
      <span className="ddp-person__text">
        <span className="ddp-person__name">{name || "未命名"}</span>
        {sub && <span className="ddp-person__sub mono">{sub}</span>}
      </span>
    </>
  );
  return onClick ? (
    <button type="button" className="ddp-person is-link" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <span className="ddp-person">{inner}</span>
  );
};

export const TwoLine: React.FC<{ main: React.ReactNode; sub?: React.ReactNode; mono?: boolean }> = ({ main, sub, mono }) => (
  <span className="ddp-two-line">
    <span className={mono ? "mono" : undefined}>{main}</span>
    {sub && <small className={mono ? "mono" : undefined}>{sub}</small>}
  </span>
);

export const Mono: React.FC<{ children: React.ReactNode; muted?: boolean }> = ({ children, muted }) => (
  <span className={`mono${muted ? " text-muted" : ""}`}>{children}</span>
);

/* ---------------- 空状态 ---------------- */
export const EmptyState: React.FC<{ title: string; desc?: string; action?: React.ReactNode; icon?: IconName }> = ({ title, desc, action, icon = "file" }) => (
  <div className="ddp-empty">
    <span className="ddp-empty__icon"><Icon name={icon} size={22} /></span>
    <strong>{title}</strong>
    {desc && <p>{desc}</p>}
    {action}
  </div>
);

/* ---------------- 提示条 ---------------- */
export const Callout: React.FC<{ tone?: Tone; icon?: IconName; title?: React.ReactNode; children?: React.ReactNode; action?: React.ReactNode }> = ({
  tone = "ver",
  icon = "alert",
  title,
  children,
  action,
}) => (
  <div className={`ddp-callout ddp-callout--${tone}`} role="status">
    <Icon name={icon} size={18} />
    <div className="ddp-callout__text">
      {title && <strong>{title}</strong>}
      {children && <span>{children}</span>}
    </div>
    {action}
  </div>
);

/* ---------------- 确认对话框 ---------------- */
export const ConfirmDialog: React.FC<{
  open: boolean;
  title: React.ReactNode;
  icon?: IconName;
  tone?: Tone;
  children?: React.ReactNode;
  confirmText?: string;
  confirmIcon?: IconName;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, icon = "alert", tone = "ver", children, confirmText = "确定", confirmIcon, danger = true, loading, onConfirm, onCancel }) => (
  <Modal
    open={open}
    onCancel={onCancel}
    footer={null}
    width={460}
    rootClassName="ddp-confirm"
    destroyOnClose
    centered
  >
    <div className="ddp-confirm__head">
      <span className={`ddp-confirm__icon ddp-confirm__icon--${tone}`}><Icon name={icon} size={20} /></span>
      <h2>{title}</h2>
    </div>
    {children && <div className="ddp-confirm__body">{children}</div>}
    <div className="ddp-confirm__footer">
      <Button onClick={onCancel}>取消</Button>
      <Button type="primary" danger={danger} loading={loading} onClick={onConfirm} icon={confirmIcon ? <Icon name={confirmIcon} /> : undefined}>
        {confirmText}
      </Button>
    </div>
  </Modal>
);

/* ---------------- 抽屉页脚 ---------------- */
export const DrawerFooter: React.FC<{ children: React.ReactNode; note?: React.ReactNode }> = ({ children, note }) => (
  <div className="ddp-drawer-footer">
    {note && <span className="ddp-drawer-footer__note">{note}</span>}
    <div className="ddp-drawer-footer__actions">{children}</div>
  </div>
);

export const formatTime = (value?: string | null, withSeconds = false) => {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid()) return value;
  return d.format(withSeconds ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD HH:mm");
};

/** 今天 14:32 / 昨天 09:10 / 09-21 16:44 / 2025-12-01 */
export const formatRelative = (value?: string | null) => {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid()) return value;
  const today = dayjs().startOf("day");
  if (d.isAfter(today)) return `今天 ${d.format("HH:mm")}`;
  if (d.isAfter(today.subtract(1, "day"))) return `昨天 ${d.format("HH:mm")}`;
  if (d.year() === today.year()) return d.format("MM-DD HH:mm");
  return d.format("YYYY-MM-DD");
};

export const formatNumber = (value?: number | null) => (value === undefined || value === null ? "—" : Number(value).toLocaleString("zh-CN"));
